/* About page only. Original media, native scrolling, and static motion fallbacks. */
if (!customElements.get('monument-about-v2')) {
  customElements.define('monument-about-v2', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      const on = (target, event, fn, options = {}) => target?.addEventListener(event, fn, {...options, signal});
      const q = selector => this.querySelector(selector);
      const all = selector => [...this.querySelectorAll(selector)];
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      const desktop = matchMedia('(min-width: 960px) and (min-height: 700px) and (hover: hover) and (pointer: fine)');
      const dialog = q('.mga-video-dialog'), video = q('[data-story-video]');
      const heroVideo = q('[data-hero-video]'), heroToggle = q('[data-hero-toggle]');
      let wanted = !reduced.matches && !navigator.connection?.saveData, visible = true;
      heroVideo.muted = heroVideo.defaultMuted = true;
      heroToggle.hidden = false;
      const updateVideoButton = () => {
        const playing = !heroVideo.paused && !heroVideo.ended;
        heroToggle.setAttribute('aria-label', playing ? 'Pause background video' : 'Play background video');
        q('[data-hero-toggle-label]').textContent = playing ? 'Pause video' : 'Play video';
        q('[data-hero-toggle-icon]').textContent = playing ? 'Ⅱ' : '▶';
      };
      const syncVideo = () => {
        if (signal.aborted) return;
        if (wanted && visible && !document.hidden && !dialog.open && heroVideo.dataset.src) {
          if (!heroVideo.src) heroVideo.src = heroVideo.dataset.src;
          heroVideo.play().catch(updateVideoButton);
        } else heroVideo.pause();
      };
      on(heroToggle, 'click', () => {wanted = heroVideo.paused; syncVideo();});
      on(heroVideo, 'playing', () => {heroVideo.classList.add('is-playing'); updateVideoButton();});
      on(heroVideo, 'pause', updateVideoButton);
      on(heroVideo, 'error', () => {heroVideo.classList.remove('is-playing'); updateVideoButton();});
      this.heroObserver = new IntersectionObserver(entries => {visible = entries[0].isIntersecting; syncVideo();});
      this.heroObserver.observe(q('.mga-hero'));
      on(document, 'visibilitychange', syncVideo);
      const opener = q('[data-open-video]');
      on(opener, 'click', () => {if (!video.src) video.src = video.dataset.src; dialog.showModal(); syncVideo();});
      on(q('[data-close-video]'), 'click', () => dialog.close());
      // Theme-wide keyboard handlers must not suppress this dialog's Escape action.
      on(window, 'keydown', event => {
        if (event.key === 'Escape' && dialog.open) {event.preventDefault(); dialog.close();}
      }, {capture:true});
      on(dialog, 'close', () => {video.pause(); syncVideo(); opener.focus({preventScroll:true});});
      on(dialog, 'click', event => {
        if (event.target !== dialog) return;
        const r = dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close();
      });
      on(reduced, 'change', () => {wanted = !reduced.matches && !navigator.connection?.saveData; syncVideo();});
      updateVideoButton();

      const section = q('#mga-milestones'), pin = q('.mga-timeline-pin'), track = q('.mga-timeline-track');
      const milestones = [...track.children], years = all('[data-milestone]');
      const prev = q('[data-timeline-prev]'), next = q('[data-timeline-next]'), nav = q('.mga-chapter-nav');
      let active = 0, pinned = false, headerHeight = 0, sceneStart = 0, sceneSpan = 1;
      const indexAt = distance => Math.max(0, Math.min(milestones.length - 1, Math.floor(Math.max(0, distance) / sceneSpan * milestones.length)));
      const show = index => {
        active = Math.max(0, Math.min(milestones.length - 1, index));
        years.forEach((button, i) => {if (i === active) button.setAttribute('aria-current', 'step'); else button.removeAttribute('aria-current');});
        q('[data-timeline-count]').textContent = String(active + 1).padStart(2, '0');
        q('[data-active-year]').textContent = milestones[active].querySelector('time').dateTime;
        prev.disabled = active === 0; next.disabled = active === milestones.length - 1;
      };
      const measure = () => {
        sceneStart = scrollY + section.getBoundingClientRect().top - nav.getBoundingClientRect().height - headerHeight;
        sceneSpan = Math.max(1, section.offsetHeight - pin.offsetHeight);
      };
      const setHeader = () => {
        const header = document.querySelector('sticky-header') || document.querySelector('.cc-header');
        const rect = header?.getBoundingClientRect();
        const height = rect ? Math.ceil(Math.max(0, Math.min(rect.bottom, rect.height))) : 0;
        const changed = height !== headerHeight;
        headerHeight = height;
        this.style.setProperty('--mga-header-offset', height + 'px');
        return changed;
      };
      const configure = () => {
        if (signal.aborted || !milestones.length) return;
        setHeader();
        const offset = Math.ceil(nav.getBoundingClientRect().height) + headerHeight;
        const available = innerHeight - offset;
        const eligible = desktop.matches && !reduced.matches;
        section.style.setProperty('--chapter-height', offset + 'px');
        section.style.setProperty('--pin-height', available + 'px');
        section.style.setProperty('--timeline-distance', milestones.length * Math.min(480, Math.max(300, available * .52)) + 'px');
        section.classList.toggle('is-scroll-timeline', eligible);
        pinned = eligible && pin.scrollHeight <= available + 1;
        section.classList.toggle('is-scroll-timeline', pinned);
        if (pinned) {measure(); show(indexAt(scrollY - sceneStart));}
        track.scrollTo({left:active * track.clientWidth, behavior:'instant'});
      };
      const choose = index => {
        const i = Math.max(0, Math.min(milestones.length - 1, index));
        if (pinned) {
          measure();
          window.scrollTo({top:sceneStart + (i + .08) / milestones.length * sceneSpan, behavior:'instant'});
          show(i); track.scrollTo({left:i * track.clientWidth, behavior:'instant'});
        } else track.scrollTo({left:i * track.clientWidth, behavior:reduced.matches ? 'instant' : 'smooth'});
      };
      years.forEach((button,i) => on(button,'click', () => choose(i)));
      on(prev,'click', () => choose(active - 1)); on(next,'click', () => choose(active + 1));
      on(track,'scroll', () => {if (!pinned && milestones.length) show(Math.round(track.scrollLeft / Math.max(1,track.clientWidth)));}, {passive:true});
      on(track,'keydown', event => {
        if (event.key === 'ArrowRight') {event.preventDefault(); choose(active + 1);}
        if (event.key === 'ArrowLeft') {event.preventDefault(); choose(active - 1);}
      });
      on(window,'scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = 0;
          if (setHeader()) configure();
          if (!pinned) return;
          measure(); const i = indexAt(scrollY - sceneStart);
          if (i !== active) {show(i); track.scrollTo({left:i * track.clientWidth,behavior:'instant'});}
        });
      }, {passive:true});
      const scheduleLayout = () => {cancelAnimationFrame(this.layoutFrame); this.layoutFrame = requestAnimationFrame(configure);};
      on(window,'resize',scheduleLayout,{passive:true});
      on(desktop,'change',configure); on(reduced,'change',configure);
      this.resizeObserver = new ResizeObserver(scheduleLayout);
      this.resizeObserver.observe(nav);
      configure(); document.fonts.ready.then(configure);
      const chapters = all('.mga-chapter-nav a');
      this.chapterObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) chapters.forEach(link => {
            const current = link.hash === '#' + entry.target.id;
            link.classList.toggle('active',current);
            if (current) link.setAttribute('aria-current','location'); else link.removeAttribute('aria-current');
          });
        });
      }, {rootMargin:'-10% 0px -65% 0px'});
      chapters.forEach(link => {const chapter=q(link.hash); if(chapter) this.chapterObserver.observe(chapter);});
      on(this,'shopify:block:select', event => {
        const index = milestones.findIndex(element => element === event.target || element.contains(event.target));
        if (index >= 0) choose(index);
      });
    }
    disconnectedCallback() {
      this.abort?.abort(); this.abort = null;
      this.heroObserver?.disconnect(); this.chapterObserver?.disconnect(); this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.scrollFrame); cancelAnimationFrame(this.layoutFrame);
      this.querySelectorAll('video').forEach(video=>video.pause());
      this.querySelector('dialog')?.close();
    }
  });
}
